import React from 'react';

// Before a story plays: its cover as a framed picture, tilted a touch, over a
// blurred, color-matched copy of itself (the cover is square, the player is
// 16:9, so this fills the sides instead of leaving bars), with the big play
// button on top. A story with only the placeholder gradient for a cover shows
// the Mux poster (a real frame from the video) full-bleed instead.

const PLACEHOLDER = /\/assets\/gradient\.png(\?|$)/;
const FALLBACK = '/assets/pixley-panda-nobg.png';

export const isPlaceholderCover = (url) => !url || PLACEHOLDER.test(url);

const css = `
.pxc-frame { transition: transform 260ms cubic-bezier(0.34,1.56,0.64,1); }
.pxc:hover .pxc-frame { transform: translate(-50%, -50%) rotate(0deg) scale(1.03) !important; }
.pxc-play { transition: transform 260ms cubic-bezier(0.34,1.56,0.64,1); }
.pxc:hover .pxc-play { transform: translate(-50%, -50%) scale(1.08) !important; }
.pxc:active .pxc-play { transform: translate(-50%, -50%) scale(0.92) !important; }
@keyframes pxcSpin { to { transform: rotate(360deg); } }
.pxc-spin { animation: pxcSpin 800ms linear infinite; }
@media (prefers-reduced-motion: reduce) { .pxc-frame, .pxc-play { transition: none; } .pxc-spin { animation-duration: 2.4s; } }
`;

export default function CoverPoster({ cover, posterUrl, title = '', onPlay, portrait = false, loading = false }) {
  const placeholder = isPlaceholderCover(cover);
  const fullBleed = placeholder && posterUrl;
  const art = fullBleed ? posterUrl : placeholder ? FALLBACK : cover;

  return (
    <div
      className="pxc"
      role="button"
      tabIndex={0}
      aria-label={title ? `Play ${title}` : 'Play story'}
      onClick={onPlay}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onPlay();
        }
      }}
      style={{ position: 'absolute', inset: 0, overflow: 'hidden', cursor: 'pointer', background: '#EAF3FF', zIndex: 5 }}
    >
      <style>{css}</style>
      {fullBleed ? (
        <img src={art} alt="" draggable={false} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : (
        <>
          {!placeholder && (
            <div
              aria-hidden="true"
              style={{
                position: 'absolute',
                inset: -40,
                backgroundImage: `url("${art}")`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                filter: 'blur(32px) saturate(1.3)',
                transform: 'scale(1.12)',
              }}
            />
          )}
          <div aria-hidden="true" style={{ position: 'absolute', inset: 0, background: 'rgba(11,26,51,0.12)' }} />
          <div
            className="pxc-frame"
            style={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              [portrait ? 'width' : 'height']: '84%',
              aspectRatio: '1 / 1',
              maxWidth: '88%',
              maxHeight: '88%',
              transform: 'translate(-50%, -50%) rotate(2deg)',
              background: '#FFFFFF',
              padding: 'clamp(5px, 1.1%, 9px)',
              borderRadius: 8,
              boxShadow: '0 18px 40px rgba(0,0,0,0.3)',
            }}
          >
            <img
              src={art}
              alt=""
              draggable={false}
              onError={(e) => {
                e.currentTarget.src = FALLBACK;
              }}
              style={{ width: '100%', height: '100%', objectFit: placeholder ? 'contain' : 'cover', borderRadius: 4, display: 'block', background: '#EAF3FF' }}
            />
          </div>
        </>
      )}
      <span
        className="pxc-play"
        aria-hidden="true"
        style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          width: 'clamp(64px, 11%, 92px)',
          aspectRatio: '1 / 1',
          borderRadius: '50%',
          background: 'linear-gradient(180deg, #2F8BFF 0%, #0065F8 100%)',
          boxShadow: '0 5px 0 #073B8E, 0 18px 40px rgba(0,101,248,0.4)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {loading ? (
          <svg className="pxc-spin" viewBox="0 0 24 24" fill="none" style={{ width: '46%', height: '46%' }} aria-label="Loading">
            <circle cx="12" cy="12" r="9" stroke="rgba(255,255,255,0.35)" strokeWidth="3" />
            <path d="M21 12a9 9 0 00-9-9" stroke="#FFFFFF" strokeWidth="3" strokeLinecap="round" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="#FFFFFF" style={{ width: '42%', height: '42%', marginLeft: '8%' }}>
            <path d="M8 5v14l11-7z" />
          </svg>
        )}
      </span>
    </div>
  );
}
